import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Select, SimpleGrid, Spinner, Badge,
  Stat, StatLabel, StatNumber, Icon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiTrendingUp, FiActivity, FiZap, FiUsers } from 'react-icons/fi';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';

const API_BASE = '/api/v1/admin-ui';
const COLORS = ['#DD6B20', '#38A169', '#3182CE', '#D69E2E', '#E53E3E', '#805AD5', '#319795', '#63B3ED'];

function UsageAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Token ${localStorage.getItem('token')}`,
    };
    const instanceId = localStorage.getItem('instance_id');
    if (instanceId) headers['X-Tenant-ID'] = instanceId;
    return headers;
  };

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/analytics/?days=${days}`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="orange.500" /></Box>;
  if (!data) return <Text color="gray.500" textAlign="center" py={20}>Failed to load analytics</Text>;

  const totalProfiles = (data.profiles_by_day || []).reduce((s, a) => s + a.count, 0);
  const totalEvents = (data.events_by_day || []).reduce((s, a) => s + a.count, 0);
  const totalRegistrations = (data.registrations_by_day || []).reduce((s, a) => s + a.count, 0);
  const totalEmails = (data.emails_by_day || []).reduce((s, a) => s + a.count, 0);

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Usage Analytics</Heading>
          <Text color="gray.500">Activity trends and usage patterns</Text>
        </VStack>
        <HStack>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))} w="140px" bg="white">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </Select>
          <Button leftIcon={<FiRefreshCw />} colorScheme="orange" onClick={fetchAnalytics}>Refresh</Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="orange.100" borderRadius="lg">
              <Icon as={FiUsers} boxSize={6} color="orange.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">New Profiles</StatLabel>
              <StatNumber fontSize="2xl">{totalProfiles}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="blue.100" borderRadius="lg">
              <Icon as={FiActivity} boxSize={6} color="blue.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">New Events</StatLabel>
              <StatNumber fontSize="2xl">{totalEvents}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="green.100" borderRadius="lg">
              <Icon as={FiTrendingUp} boxSize={6} color="green.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Registrations</StatLabel>
              <StatNumber fontSize="2xl">{totalRegistrations}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
        <Stat bg="white" p={6} borderRadius="lg" boxShadow="md">
          <HStack>
            <Box p={3} bg="purple.100" borderRadius="lg">
              <Icon as={FiZap} boxSize={6} color="purple.600" />
            </Box>
            <VStack align="start" spacing={0}>
              <StatLabel color="gray.500">Emails Sent</StatLabel>
              <StatNumber fontSize="2xl">{totalEmails}</StatNumber>
            </VStack>
          </HStack>
        </Stat>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Profile Growth</Heading>
          {data.profiles_by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={data.profiles_by_day}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Area type="monotone" dataKey="count" stroke="#DD6B20" fill="#DD6B20" fillOpacity={0.2} name="Profiles" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No profile data</Text>}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Events &amp; Registrations</Heading>
          {(data.events_by_day?.length > 0 || data.registrations_by_day?.length > 0) ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={mergeTimeSeries(data.events_by_day, data.registrations_by_day)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Legend />
                <Bar dataKey="events" fill="#3182CE" name="Events" />
                <Bar dataKey="registrations" fill="#38A169" name="Registrations" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No data</Text>}
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6} mb={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Activity Types</Heading>
          {data.activity_by_type?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={data.activity_by_type} dataKey="count" nameKey="activity_type" cx="50%" cy="50%" outerRadius={80} label={({activity_type, count}) => `${activity_type} (${count})`}>
                  {data.activity_by_type.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No data</Text>}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Admin Types</Heading>
          <VStack align="stretch" spacing={2}>
            {(data.admin_type_breakdown || []).map((s, i) => (
              <HStack key={i} justify="space-between">
                <Badge colorScheme={
                  s.admin_type === 'system_administrator' ? 'red' :
                  s.admin_type === 'account_admin' ? 'purple' :
                  s.admin_type === 'event_host' ? 'blue' : 'gray'
                }>
                  {s.admin_type}
                </Badge>
                <Text fontWeight="bold">{s.count}</Text>
              </HStack>
            ))}
            {(!data.admin_type_breakdown || data.admin_type_breakdown.length === 0) && (
              <Text color="gray.400" textAlign="center" py={4}>No admins</Text>
            )}
          </VStack>
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Profile Statuses</Heading>
          <VStack align="stretch" spacing={2}>
            {(data.profile_statuses || []).map((s, i) => (
              <HStack key={i} justify="space-between">
                <Badge colorScheme={s.status === 'active' ? 'green' : s.status === 'blocked' ? 'red' : 'gray'}>
                  {s.status}
                </Badge>
                <Text fontWeight="bold">{s.count}</Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>User Activity</Heading>
          {data.activity_by_day?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.activity_by_day}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Area type="monotone" dataKey="count" stroke="#805AD5" fill="#805AD5" fillOpacity={0.2} name="Activity" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No activity data</Text>}
        </Box>

        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          <Heading size="sm" mb={4}>Email &amp; Webhook Volume</Heading>
          {(data.emails_by_day?.length > 0 || data.webhooks_by_day?.length > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={mergeEmailWebhook(data.emails_by_day, data.webhooks_by_day)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={formatDate} fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} />
                <Legend />
                <Bar dataKey="emails" fill="#DD6B20" name="Emails" />
                <Bar dataKey="webhooks" fill="#D69E2E" name="Webhooks" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Text color="gray.400" textAlign="center" py={10}>No data</Text>}
        </Box>
      </SimpleGrid>
    </Box>
  );
}

function mergeTimeSeries(series1 = [], series2 = []) {
  const map = {};
  for (const item of series1) map[item.date] = { date: item.date, events: item.count, registrations: 0 };
  for (const item of series2) {
    if (!map[item.date]) map[item.date] = { date: item.date, events: 0, registrations: item.count };
    else map[item.date].registrations = item.count;
  }
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

function mergeEmailWebhook(series1 = [], series2 = []) {
  const map = {};
  for (const item of series1) map[item.date] = { date: item.date, emails: item.count, webhooks: 0 };
  for (const item of series2) {
    if (!map[item.date]) map[item.date] = { date: item.date, emails: 0, webhooks: item.count };
    else map[item.date].webhooks = item.count;
  }
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}

export default UsageAnalytics;
